/**
 * infoCardPdfExport.js — A4 信息卡 PDF 导出（Client / Contact / Project 共用）
 *
 * 使用方式：
 *   import { exportClientPdf, exportContactPdf, exportProjectPdf } from './infoCardPdfExport';
 *   exportClientPdf(clientData, companyInfo);   // 预览/下载
 */

import jsPDF from 'jspdf';
import { durationDays } from './scheduleAdapter';
import { LOGO_FALLBACK_URL } from './defaultData';

/**
 * Duration text for one task in the project PDF.
 *
 * Prefers the absolute dates. Falls back to the deprecated `duration` only for
 * tasks that have never been scheduled, and says "not scheduled" when there is
 * genuinely nothing to report rather than inventing a number.
 */
export function taskDurationLabel(task) {
  const days = durationDays(task?.start_date, task?.end_date);
  if (days != null) return `${days} ${days === 1 ? 'day' : 'days'}`;
  if (task?.duration > 0) return `${task.duration} ${task.duration === 1 ? 'day' : 'days'}`;
  return 'not scheduled';
}
import { getBRAND_NAME, getBRAND_FULL, getBRAND_ACN } from './brandConfig';
import {
  NAVY, GOLD, TEXT_COLOR, MID_GRAY, WHITE, PAGE, CONTENT_WIDTH,
  loadImageAsBase64, ensureSpace, addInfoCardFooters,
} from './pdfShared';

// === Utilities: imported from pdfShared.js ===
// loadImageAsBase64, ensureSpace — all in pdfShared

// === Header ===

async function renderHeader(doc, title, logoUrl) {
  const HEADER_H = 42;
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE.width, HEADER_H, 'F');
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(0, HEADER_H, PAGE.width, HEADER_H);

  // LOGO
  let logoBase64 = null;
  if (logoUrl) {
    logoBase64 = await loadImageAsBase64(logoUrl, LOGO_FALLBACK_URL);
  }
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', PAGE.margin, 8, 24, 24);
    } catch (_) { /* skip if image fails */ }
  }

  // Title
  doc.setFontSize(18);
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.text(title, logoBase64 ? PAGE.margin + 30 : PAGE.margin, 26);

  return HEADER_H + 8;
}

// === Info row ===

function renderInfoRow(doc, y, label, value, x = PAGE.margin) {
  doc.setFontSize(8);
  doc.setTextColor(...MID_GRAY);
  doc.setFont('helvetica', 'bold');
  doc.text(label.toUpperCase(), x, y);

  doc.setFontSize(10);
  doc.setTextColor(...TEXT_COLOR);
  doc.setFont('helvetica', 'normal');
  doc.text(String(value || '—'), x, y + 5);

  return y + 14;
}

// === Info grid (2 columns) ===

function renderInfoGrid(doc, y, fields) {
  const colWidth = CONTENT_WIDTH / 2;
  const rowH = 16;
  let currentY = y;

  for (let i = 0; i < fields.length; i += 2) {
    currentY = ensureSpace(doc, currentY, rowH + 4, 20);

    // Left column
    const left = fields[i];
    doc.setFontSize(7);
    doc.setTextColor(...MID_GRAY);
    doc.setFont('helvetica', 'bold');
    doc.text(left.label.toUpperCase(), PAGE.margin, currentY);
    doc.setFontSize(10);
    doc.setTextColor(...TEXT_COLOR);
    doc.setFont('helvetica', 'normal');
    const leftVal = String(left.value || '—');
    const leftLines = doc.splitTextToSize(leftVal, colWidth - 6);
    doc.text(leftLines, PAGE.margin, currentY + 4.5);

    // Right column
    if (fields[i + 1]) {
      const right = fields[i + 1];
      const rightX = PAGE.margin + colWidth;
      doc.setFontSize(7);
      doc.setTextColor(...MID_GRAY);
      doc.setFont('helvetica', 'bold');
      doc.text(right.label.toUpperCase(), rightX, currentY);
      doc.setFontSize(10);
      doc.setTextColor(...TEXT_COLOR);
      doc.setFont('helvetica', 'normal');
      const rightVal = String(right.value || '—');
      const rightLines = doc.splitTextToSize(rightVal, colWidth - 6);
      doc.text(rightLines, rightX, currentY + 4.5);
    }

    // Calculate the max height of this row
    const leftH = leftLines ? leftLines.length * 5 : 5;
    const rightLines2 = fields[i + 1] ? doc.splitTextToSize(String(fields[i + 1].value || '—'), colWidth - 6) : [];
    const rightH = rightLines2.length * 5;
    currentY += Math.max(leftH, rightH) + 10;
  }

  return currentY;
}

// === Section divider ===

function renderSectionTitle(doc, y, title) {
  y = ensureSpace(doc, y, 20, 20);
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, y, CONTENT_WIDTH, 0.5, 'F');
  y += 6;
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), PAGE.margin, y + 3);
  return y + 10;
}

// addInfoCardFooters replaced by addInfoCardFooters from pdfShared.js

// ═══════════════════════════════════════════════════════════════════════════════
// CLIENT PDF
// ═══════════════════════════════════════════════════════════════════════════════

export async function generateClientPdf(client, companyInfo) {
  const doc = new jsPDF('p', 'mm', 'a4');
  let y = await renderHeader(doc, 'Client Information', companyInfo?.logo_url);

  // Client number badge
  y += 4;
  doc.setFontSize(10);
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.text(client.client_no || '', PAGE.margin, y);
  doc.setTextColor(...TEXT_COLOR);
  doc.setFont('helvetica', 'normal');
  y += 10;

  // Section: Company
  y = renderSectionTitle(doc, y, 'Company Details');
  y = renderInfoGrid(doc, y, [
    { label: 'Company Name', value: client.company_name },
    { label: 'Trading Name', value: client.trading_name },
    { label: 'ABN', value: client.abn },
    { label: 'Industry', value: client.industry },
    { label: 'Status', value: client.status?.toUpperCase() },
  ]);

  // Section: Location
  y = renderSectionTitle(doc, y, 'Location');
  y = renderInfoGrid(doc, y, [
    { label: 'Address', value: client.address },
    { label: 'Website', value: client.website },
  ]);

  // Section: Notes
  if (client.notes) {
    y = renderSectionTitle(doc, y, 'Notes');
    y = ensureSpace(doc, y, 20, 20);
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(client.notes, CONTENT_WIDTH);
    for (const line of lines) {
      y = ensureSpace(doc, y, 5, 20);
      doc.text(line, PAGE.margin, y);
      y += 5;
    }
  }

  // Section: Company info (our side)
  y = renderSectionTitle(doc, y, getBRAND_NAME());
  y = renderInfoGrid(doc, y, [
    { label: 'Company', value: getBRAND_FULL() },
    { label: 'ACN', value: getBRAND_ACN() },
    { label: 'Phone', value: companyInfo?.phone || '' },
    { label: 'Director', value: companyInfo?.director || '' },
  ]);

  addInfoCardFooters(doc, companyInfo);
  return doc;
}

// ── Convenience functions ─────────────────────────────────────────────────────

export async function previewClientPdf(client, companyInfo) {
  const doc = await generateClientPdf(client, companyInfo);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

export async function downloadClientPdf(client, companyInfo) {
  const doc = await generateClientPdf(client, companyInfo);
  const fileName = `${client.client_no || 'client'}_${client.company_name?.replace(/\s+/g, '_') || 'info'}.pdf`;
  doc.save(fileName);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTACT PDF (placeholder for Phase 5)
// ═══════════════════════════════════════════════════════════════════════════════

export async function generateContactPdf(contact, companyInfo) {
  const doc = new jsPDF('p', 'mm', 'a4');
  let y = await renderHeader(doc, 'Contact Information', companyInfo?.logo_url);

  y += 4;
  doc.setFontSize(10);
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.text(contact.contact_no || '', PAGE.margin, y);
  doc.setTextColor(...TEXT_COLOR);
  doc.setFont('helvetica', 'normal');
  y += 10;

  y = renderSectionTitle(doc, y, 'Personal Details');
  y = renderInfoGrid(doc, y, [
    { label: 'Full Name', value: contact.full_name },
    { label: 'Title', value: contact.title },
    { label: 'Category', value: contact.category },
    { label: 'Company', value: contact.company },
  ]);

  y = renderSectionTitle(doc, y, 'Contact');
  y = renderInfoGrid(doc, y, [
    { label: 'Email', value: contact.email },
    { label: 'Phone', value: contact.phone },
    { label: 'Mobile', value: contact.mobile },
    { label: 'Website', value: contact.website },
  ]);

  if (contact.notes) {
    y = renderSectionTitle(doc, y, 'Notes');
    y = ensureSpace(doc, y, 20, 20);
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(contact.notes, CONTENT_WIDTH);
    for (const line of lines) {
      y = ensureSpace(doc, y, 5, 20);
      doc.text(line, PAGE.margin, y);
      y += 5;
    }
  }

  y = renderSectionTitle(doc, y, getBRAND_NAME());
  y = renderInfoGrid(doc, y, [
    { label: 'Company', value: getBRAND_FULL() },
    { label: 'ACN', value: getBRAND_ACN() },
  ]);

  addInfoCardFooters(doc, companyInfo);
  return doc;
}

export async function previewContactPdf(contact, companyInfo) {
  const doc = await generateContactPdf(contact, companyInfo);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

export async function downloadContactPdf(contact, companyInfo) {
  const doc = await generateContactPdf(contact, companyInfo);
  const fileName = `${contact.contact_no || 'contact'}_${contact.full_name?.replace(/\s+/g, '_') || 'info'}.pdf`;
  doc.save(fileName);
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROJECT PDF (placeholder for Phase 6)
// ═══════════════════════════════════════════════════════════════════════════════

export async function generateProjectPdf(project, companyInfo) {
  const doc = new jsPDF('p', 'mm', 'a4');
  let y = await renderHeader(doc, 'Project Information', companyInfo?.logo_url);

  y += 4;
  doc.setFontSize(10);
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.text(project.project_no || '', PAGE.margin, y);
  doc.setTextColor(...TEXT_COLOR);
  doc.setFont('helvetica', 'normal');
  y += 10;

  y = renderSectionTitle(doc, y, 'Project Details');
  y = renderInfoGrid(doc, y, [
    { label: 'Project Name', value: project.project_name },
    { label: 'Status', value: project.status?.toUpperCase() },
    { label: 'Client', value: project.client_name || '—' },
    { label: 'Site Address', value: project.site_address },
    { label: 'Start Date', value: project.start_date },
    { label: 'End Date', value: project.end_date },
  ]);

  if (project.description) {
    y = renderSectionTitle(doc, y, 'Description');
    y = ensureSpace(doc, y, 20, 20);
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_COLOR);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(project.description, CONTENT_WIDTH);
    for (const line of lines) {
      y = ensureSpace(doc, y, 5, 20);
      doc.text(line, PAGE.margin, y);
      y += 5;
    }
  }

  // Tasks summary.
  // Duration comes from the absolute dates, NOT the legacy `duration` field:
  // scheduling a task through the Schedule tab writes start_date/end_date and
  // leaves the deprecated `duration` at its old value, so reading it would
  // print "1 days" for a five-day task.
  if (project.tasks && project.tasks.length > 0) {
    y = renderSectionTitle(doc, y, `Tasks (${project.tasks.length})`);
    y = ensureSpace(doc, y, 20, 20);
    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    doc.setFont('helvetica', 'bold');
    doc.text('TRADE', PAGE.margin, y);
    doc.text('TASK', PAGE.margin + 40, y);
    doc.text('DURATION', PAGE.margin + 130, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_COLOR);
    for (const t of project.tasks.slice(0, 20)) {
      y = ensureSpace(doc, y, 6, 20);
      doc.text(String(t.trade || '—'), PAGE.margin, y);
      doc.text(String(t.task_name || '—'), PAGE.margin + 40, y);
      doc.text(taskDurationLabel(t), PAGE.margin + 130, y);
      y += 5;
    }
    if (project.tasks.length > 20) {
      doc.setTextColor(...MID_GRAY);
      doc.text(`... and ${project.tasks.length - 20} more tasks`, PAGE.margin, y + 2);
    }
  }

  y = renderSectionTitle(doc, y, getBRAND_NAME());
  y = renderInfoGrid(doc, y, [
    { label: 'Company', value: getBRAND_FULL() },
    { label: 'ACN', value: getBRAND_ACN() },
  ]);

  addInfoCardFooters(doc, companyInfo);
  return doc;
}

export async function previewProjectPdf(project, companyInfo) {
  const doc = await generateProjectPdf(project, companyInfo);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

export async function downloadProjectPdf(project, companyInfo) {
  const doc = await generateProjectPdf(project, companyInfo);
  const fileName = `${project.project_no || 'project'}_${project.project_name?.replace(/\s+/g, '_') || 'info'}.pdf`;
  doc.save(fileName);
}
